/**
 * O vocabulário visual da mesa: paleta, ícones e as três funções que todo
 * componente usa.
 *
 * O estilo veio do Claude Design escrito como CSS de verdade (`display:flex;
 * gap:10px`), e é assim que ele fica aqui. `s()` traduz essa string para o
 * objeto que o React espera — o desenho continua legível ao lado do original,
 * em vez de virar duzentos objetos reescritos à mão, que é onde se perde uma
 * vírgula e ninguém acha.
 *
 * Os ícones são do Hugeicons (`@hugeicons/react` + o pacote livre). Um traço
 * só para a tela inteira, e um nome de ícone que se lê — melhor que o caminho
 * SVG copiado do desenho, que ninguém consegue conferir de olho.
 */
import { HugeiconsIcon } from "@hugeicons/react";
import {
  AiMagicIcon,
  Megaphone01Icon,
  Alert02Icon,
  ArrowDown01Icon,
  ArrowLeft01Icon,
  ArrowRight01Icon,
  Attachment01Icon,
  Tag01Icon,
  Calendar03Icon,
  DashboardSquare01Icon,
  ChatBotIcon,
  RoboticIcon,
  CheckListIcon,
  BubbleChatIcon,
  Cancel01Icon,
  CheckmarkCircle02Icon,
  Clock01Icon,
  ContactBookIcon,
  CourtHouseIcon,
  DocumentAttachmentIcon,
  File01Icon,
  FolderOpenIcon,
  LegalDocument01Icon,
  LockIcon,
  Logout03Icon,
  Mic01Icon,
  MoreVerticalIcon,
  PauseIcon,
  Notification03Icon,
  PlayIcon,
  RecordIcon,
  Download04Icon,
  Search01Icon,
  SentIcon,
  Settings02Icon,
  SidebarLeft01Icon,
  SidebarRight01Icon,
  SparklesIcon,
  StarIcon,
  StickyNote01Icon,
  Tick02Icon,
  UserGroupIcon,
  UserCircleIcon,
  HourglassIcon,
  WhatsappIcon,
  Video01Icon,
  WorkflowSquare03Icon,
  Archive02Icon,
  ChartBarLineIcon,
  Location01Icon,
  MessageCancel01Icon,
  Add01Icon,
  Delete02Icon,
  FlashIcon,
  Edit02Icon,
  InboxIcon,
  PinIcon,
  ReplyIcon,
  Share01Icon,
  SmileIcon,
  VolumeOffIcon,
  HelpCircleIcon,
  NewspaperIcon,
  ContractsIcon,
  Upload04Icon,
} from "@hugeicons/core-free-icons";

const cache = new Map();

/** CSS do desenho → objeto de estilo do React. Memoizado: a string se repete. */
export function s(css) {
  if (!css) return undefined;
  const guardado = cache.get(css);
  if (guardado) return guardado;
  const objeto = {};
  for (const regra of css.split(";")) {
    const corte = regra.indexOf(":");
    if (corte < 0) continue;
    const chave = regra.slice(0, corte).trim();
    const valor = regra.slice(corte + 1).trim();
    if (!chave || !valor) continue;
    objeto[chave.startsWith("--") ? chave : chave.replace(/-([a-z])/g, (_, c) => c.toUpperCase())] = valor;
  }
  cache.set(css, objeto);
  return objeto;
}

/*
 * O nome do atalho da paleta, escrito como o teclado de quem está lendo.
 *
 * `⌘K` no Mac e `Ctrl+K` no resto: atalho escrito errado é atalho que ninguém
 * usa. `userAgent` e não `platform`, que está a caminho da aposentadoria. Mora
 * aqui porque duas peças o mostram — a porta na faixa e o pé da paleta.
 */
export const ATALHO_DA_PALETA = typeof navigator !== "undefined" && /mac/i.test(navigator.userAgent) ? "\u2318K" : "Ctrl+K";

/* --------------------------------------------------------------- paleta */

export const AV = [
  "var(--color-verde-fundo)",
  "var(--color-terra)",
  "var(--color-azul)",
  "var(--color-marrom)",
  "var(--color-roxo)",
  "var(--color-verde)",
];

/*
 * As tintas GANHARAM NOME, porque agora alguém escolhe uma.
 *
 * Elas eram um array anônimo escolhido por `digital(texto)`: cor estável e
 * automática, que é o certo para tese e etapa — configuráveis no banco, e uma
 * tabela de cores aqui envelheceria. A etiqueta é outro caso: o escritório
 * inventa o rótulo E quer dizer de que cor ele é ("urgente" vermelho, "VIP"
 * roxo), e para isso a cor precisa de um nome que caiba no banco. O nome vai
 * para `tag_cores` (ver src/tags.ts, `CORES`) — o hexadecimal continua morando
 * só aqui, nos tokens do `index.css`.
 *
 * A ORDEM das chaves é a da paleta que a tela desenha, e ela tem de casar com
 * `CORES` do servidor: `etiquetas.test.jsx` prende isso.
 */
export const TINTAS_POR_NOME = {
  verde: ["color-mix(in oklab, var(--color-verde-fundo) 14%, transparent)", "var(--color-verde-fundo)"],
  terra: ["color-mix(in oklab, var(--color-terra) 14%, transparent)", "var(--color-terra-escura)"],
  azul: ["color-mix(in oklab, var(--color-azul) 14%, transparent)", "var(--color-azul)"],
  roxo: ["color-mix(in oklab, var(--color-roxo) 15%, transparent)", "var(--color-roxo)"],
  marrom: ["color-mix(in oklab, var(--color-marrom) 15%, transparent)", "var(--color-marrom)"],
};

const TINTAS = Object.values(TINTAS_POR_NOME);

/* Cor estável por texto: a mesma tese sai sempre da mesma cor, sem tabela fixa
   — as teses são configuráveis no banco e uma tabela aqui envelheceria. */
const digital = (t) => [...String(t ?? "")].reduce((a, c) => (a * 31 + c.charCodeAt(0)) % 997, 7);

/**
 * A cor de um rótulo: a ESCOLHIDA quando existe, a estável por texto quando não.
 *
 * `cor` é o nome da paleta que veio do banco (etiqueta pintada pelo escritório).
 * Nome que não existe cai no automático em vez de derrubar a linha: cor é
 * enfeite, e etiqueta pintada com nome velho não pode apagar a mesa.
 */
export const tinta = (t, cor) => TINTAS_POR_NOME[cor] ?? TINTAS[digital(t) % TINTAS.length];
export const corAvatar = (t) => AV[digital(t) % AV.length];

export const iniciais = (nome) =>
  String(nome ?? "?")
    .replace(/^(Grupo · Caso |Lead )/, "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase() || "?";

/*
 * -------------------------------------------------------------------- tempo
 *
 * O banco guarda UTC e o fuso entra na LEITURA — é a convenção que
 * `src/data.ts` documenta (`deslocamentoSql`) e que o servidor já respeita.
 *
 * A tela não respeitava: cortar a coluna crua na mão mostrava a hora de Londres em
 * toda bolha, em toda linha da lista e no selo da caixa. Uma mensagem enviada às
 * 18h aparecia como 21h — e num CRM que conta prazo por dia isso não é detalhe:
 * depois das 21h de Goiânia a conversa de hoje aparecia com a data de amanhã.
 *
 * Uma função lê a data e as outras formatam. `avisos.js` já fazia isto certo
 * (`dataDoAviso`) e agora chama daqui, para existir UMA implementação.
 */

/**
 * A data como ela vem do servidor, virada em `Date`.
 *
 * Três formatos chegam: `2026-08-21 14:30:00` (SQLite, UTC e sem fuso escrito),
 * `2026-08-21T14:30:00Z` ou com deslocamento (ISO, que se lê como está) e
 * `2026-08-21` (dia seco de prazo, que vale meio-dia local — meia-noite UTC
 * fazia o prazo de hoje aparecer como ontem para quem está em Brasília).
 */
export function emData(q) {
  const texto = String(q ?? "").trim();
  if (!texto) return null;
  const d =
    texto.length <= 10
      ? new Date(`${texto}T12:00:00`)
      : /* Fuso já escrito (Z ou ±hh:mm) manda; sem fuso, é UTC do banco. */
        new Date(/[Zz]$|[+-]\d\d:?\d\d$/.test(texto) ? texto : `${texto.replace(" ", "T")}Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}

const HORA = new Intl.DateTimeFormat("pt-BR", { hour: "2-digit", minute: "2-digit" });
const DIA_CURTO = new Intl.DateTimeFormat("pt-BR", { day: "2-digit", month: "2-digit" });
/* `sv-SE` é o atalho honesto para AAAA-MM-DD — o mesmo truque de `src/data.ts`. */
const DIA_ISO = new Intl.DateTimeFormat("sv-SE", { year: "numeric", month: "2-digit", day: "2-digit" });

/** A hora no relógio de quem está lendo: "14:32". */
export const hora = (q) => {
  const d = emData(q);
  return d ? HORA.format(d) : "";
};

/** O dia (AAAA-MM-DD) no fuso de quem lê — é por ele que a conversa se divide. */
export const diaDe = (q) => {
  const d = emData(q);
  return d ? DIA_ISO.format(d) : "";
};

/** "21/08", para o selo estreito da caixa de entrada. */
export const diaCurto = (q) => {
  const d = emData(q);
  return d ? DIA_CURTO.format(d) : "";
};

/**
 * O valor de um `<input type="datetime-local">`, no fuso de quem olha.
 *
 * O input exige exatamente "AAAA-MM-DDTHH:MM" e mostra o que recebe SEM
 * converter nada — cortar a coluna do banco (`quando.slice(0,16)`) põe a hora de
 * Londres no calendário de quem marca a reunião, e a reunião das 16h aparece
 * como 19h. `emData` faz a leitura, e o formato ISO local sai daqui.
 *
 * O par disto é `paraOServidor`: quem escreve devolve o valor local, e a
 * conversão para UTC é do servidor, onde a convenção mora (`src/data.ts`).
 */
export const paraCampoDeMomento = (q) => {
  const d = emData(q);
  return d ? `${DIA_ISO.format(d)}T${HORA_ISO.format(d)}` : "";
};

/** O "AAAA-MM-DD HH:MM" local que o servidor converte para UTC ao gravar. */
export const momentoDoCampo = (valor) =>
  String(valor ?? "")
    .replace("T", " ")
    .slice(0, 16);

/* `en-GB` com hourCycle h23 é o atalho honesto para HH:MM em 24h. */
const HORA_ISO = new Intl.DateTimeFormat("en-GB", { hour: "2-digit", minute: "2-digit", hourCycle: "h23" });

/**
 * Dia e hora juntos: "21/08/2026 20:30".
 *
 * É o selo de "quando isso aconteceu" que oito lugares da mesa escreviam como
 * `String(x).slice(0, 16)` — o texto cru do banco, em UTC, com o "T" no meio.
 */
export const dataHora = (q) => {
  const d = emData(q);
  return d ? `${DIA_LONGO_CURTO.format(d)} ${HORA.format(d)}` : "";
};

const DIA_LONGO_CURTO = new Intl.DateTimeFormat("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" });

/**
 * Só o dia: "03/02/2021".
 *
 * Campo de data da ficha (admissão, saída, protocolo) não tem hora, e imprimir
 * `2021-02-03` cru era a tela mostrando o formato do banco para quem lê em
 * português. Aqui, e não em cada componente: quem corta a data na mão devolve o
 * dia de Londres depois das 21h de Goiânia.
 */
export const dataSeca = (q) => {
  const d = emData(q);
  return d ? DIA_LONGO_CURTO.format(d) : "";
};

/** Hoje e ontem, no fuso de quem lê. Base do "HOJE"/"ONTEM" da conversa. */
export const hojeDe = (momento = new Date()) => DIA_ISO.format(momento);
export const ontemDe = (momento = new Date()) => DIA_ISO.format(new Date(momento.getTime() - 86_400_000));

/**
 * "HOJE · 22 AGO", que é como a mesa agrupa lista por dia.
 *
 * Mora aqui e não na Agenda porque duas telas agrupam pelo mesmo idioma — a
 * agenda e a fila de publicações —, e cabeçalho de dia escrito duas vezes é o
 * que envelhece só num dos dois.
 */
export function tituloDoDia(dia) {
  const agora = Date.now();
  /* `hojeDe` formata no fuso de quem lê; o dia cru em UTC punha "hoje" no lugar
     de "amanhã" depois das 21h de Goiânia. */
  const quando = emData(dia);
  const legivel = quando
    ? quando.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" }).replace(".", "").toUpperCase()
    : dia;
  if (dia === hojeDe()) return `HOJE · ${legivel}`;
  if (dia === hojeDe(new Date(agora + 86_400_000))) return `AMANHÃ · ${legivel}`;
  if (dia === hojeDe(new Date(agora - 86_400_000))) return `ONTEM · ${legivel}`;
  return legivel;
}

const DIA_SEMANA = new Intl.DateTimeFormat("pt-BR", { weekday: "short" });

/**
 * O carimbo da lista de conversas: "14:32" hoje, "ontem", "sex" nesta semana,
 * "12/08" antes disso.
 *
 * `hora()` sozinha mentia por omissão: a conversa de três semanas atrás dizia
 * "14:32" igual à de agora, e a coluna inteira parecia do dia — quem procurava
 * o caso parado não tinha como ver que estava parado.
 */
export const quandoCurto = (q) => {
  const d = emData(q);
  if (!d) return "";
  const dia = DIA_ISO.format(d);
  if (dia === hojeDe()) return HORA.format(d);
  if (dia === ontemDe()) return "ontem";
  /* Dentro da semana o NOME do dia diz mais que a data: "sex" se lê sem contar. */
  if (Date.now() - d.getTime() < 6 * 86_400_000) return DIA_SEMANA.format(d).replace(".", "");
  return DIA_CURTO.format(d);
};

/**
 * Dinheiro em português: "R$ 35.000" e "R$ 1,2 mi".
 *
 * Mora aqui pelo mesmo motivo das datas: valor de causa aparece no cartão, na
 * tabela, no gráfico e no painel, e cada tela cortando o número na mão devolve
 * "R$ 35000" numa e "35.000,00" na outra sobre o mesmo dado.
 *
 * `curto` é para o eixo do gráfico e para o número grande do painel, onde
 * "R$ 1.234.567" não cabe sem quebrar a linha.
 */
const REAL = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 });
const COMPACTO = new Intl.NumberFormat("pt-BR", { notation: "compact", maximumFractionDigits: 1 });

export const moeda = (v, { curto = false } = {}) => {
  const n = Number(v) || 0;
  return curto ? `R$ ${COMPACTO.format(n)}` : REAL.format(n);
};

/** Número simples com separador de milhar: "1.284". */
export const numero = (v) => new Intl.NumberFormat("pt-BR").format(Number(v) || 0);

/** "1 prazo" / "3 prazos" / "1.284 casos": o plural acompanha o número, que sai
 *  no formato da casa. "3 reunião(ões)" era a tela pedindo para ser lida duas vezes. */
export const plural = (n, um, muitos) => `${numero(n)} ${Number(n) === 1 ? um : muitos}`;

/** O fuso como gente fala ("Horário Padrão de Brasília"), e não o identificador
 *  IANA ("America/Sao_Paulo", com sublinhado e tudo). */
export const nomeDoFuso = (fuso) => {
  if (!fuso) return "";
  try {
    const parte = new Intl.DateTimeFormat("pt-BR", { timeZone: fuso, timeZoneName: "longGeneric" })
      .formatToParts(new Date())
      .find((p) => p.type === "timeZoneName")?.value;
    if (parte && !parte.startsWith("GMT")) return parte;
  } catch {
    /* fuso que o navegador não conhece cai no nome escrito */
  }
  return String(fuso).replace(/_/g, " ");
};

/**
 * Tamanho de arquivo com vírgula: "8,7 MB", "991 KB". O `toFixed` de cada tela
 * dava "8.7 MB" — ponto decimal, que em português é separador de milhar.
 */
const UMA_CASA = new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
export const tamanhoDoArquivo = (bytes) => {
  const b = Math.max(0, Number(bytes) || 0);
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${numero(Math.round(b / 1024))} KB`;
  if (b < 1024 ** 3) return `${UMA_CASA.format(b / 1024 / 1024)} MB`;
  return `${UMA_CASA.format(b / 1024 ** 3)} GB`;
};

export const mmss = (seg) => `${String(Math.floor(seg / 60)).padStart(2, "0")}:${String(Math.floor(seg % 60)).padStart(2, "0")}`;

/* ---------------------------------------------------------------- ícones */

/**
 * O traço da casa. Um componente só, para o dia em que a espessura mudar — e
 * o dia chegou: TRÊS tamanhos (14, 16, 20) e UM traço (1.8).
 *
 * Eram nove tamanhos entre 11 e 22 e cinco espessuras (1.6 a 2.2) escolhidos
 * tela a tela; na mesma fileira um ícone de 15 com traço 2 ao lado de um de 16
 * com 1.8 parece defeito. O tamanho pedido até 22 cai no degrau mais perto;
 * acima disso é ilustração (o vazio, a falha) e passa como veio. `largura`
 * continua aceita e é ignorada: o traço é da casa, não de quem chama.
 */
const DEGRAUS_DO_ICONE = [14, 16, 20];
export const TRACO = 1.8;

export const degrauDoIcone = (tamanho) =>
  tamanho > 22 ? tamanho : DEGRAUS_DO_ICONE.reduce((perto, d) => (Math.abs(d - tamanho) < Math.abs(perto - tamanho) ? d : perto));

export function Icone({ de, tamanho = 20, cor = "currentColor", estilo }) {
  return <HugeiconsIcon icon={de} size={degrauDoIcone(tamanho)} strokeWidth={TRACO} color={cor} style={estilo} />;
}

/** Os ícones que a mesa usa, batizados pelo que fazem aqui. */
export const ICONES = {
  chats: BubbleChatIcon,
  /* Tarefas é lista com visto, não calendário: o que a tela mostra é trabalho a
     fazer, e o prazo é uma coluna dele. */
  tarefas: CheckListIcon,
  agenda: Calendar03Icon,
  /* Rápidas é o raio: é o texto que sai sem digitar. */
  rapidas: FlashIcon,
  fluxos: WorkflowSquare03Icon,
  calls: Video01Icon,
  files: File01Icon,
  starred: StarIcon,
  /* Etiqueta é o recorte que o escritório inventa ("urgente", "audiência
     marcada") — não é a tese nem a etapa, que já têm forma própria na mesa. */
  etiqueta: Tag01Icon,
  settings: Settings02Icon,
  peca: LegalDocument01Icon,
  contrato: ContractsIcon,
  painel: SidebarRight01Icon,
  /* O par do `painel`: a coluna da ESQUERDA (a lista de atendimentos). Os dois
     desenham a mesma caixa com um lado cheio — o lado diz qual coluna é, sem
     precisar de rótulo. */
  painelEsquerdo: SidebarLeft01Icon,
  mais: MoreVerticalIcon,
  voltar: ArrowLeft01Icon,
  avancar: ArrowRight01Icon,
  baixo: ArrowDown01Icon,
  sino: Notification03Icon,
  busca: Search01Icon,
  anexo: Attachment01Icon,
  nota: StickyNote01Icon,
  enviar: SentIcon,
  ia: AiMagicIcon,
  brilho: SparklesIcon,
  microfone: Mic01Icon,
  gravando: RecordIcon,
  tocar: PlayIcon,
  pausar: PauseIcon,
  baixar: Download04Icon,
  subir: Upload04Icon,
  confere: CheckmarkCircle02Icon,
  /* O visto sozinho — o da opção escolhida e o do fato marcado. */
  certo: Tick02Icon,
  cadeado: LockIcon,
  fechar: Cancel01Icon,
  equipe: UserGroupIcon,
  /* Contatos é a AGENDA de gente de fora; equipe é o grupo de dentro. Eram o
     mesmo desenho no trilho, e dois ícones idênticos em vistas diferentes é o
     mesmo que ícone nenhum: a pessoa só descobre onde clicou depois de clicar. */
  contatos: ContactBookIcon,
  /* Uma pessoa só (o "Meus" da mesa), a ampulheta de quem espera alguém pegar
     e a marca do aparelho: os três são pastilha de filtro, e ali o ícone é a
     única palavra que cabe. */
  pessoa: UserCircleIcon,
  ampulheta: HourglassIcon,
  whatsapp: WhatsappIcon,
  relogio: Clock01Icon,
  sair: Logout03Icon,
  risco: Alert02Icon,
  pasta: FolderOpenIcon,
  documento: DocumentAttachmentIcon,
  caixa: InboxIcon,
  matriz: DashboardSquare01Icon,
  /* O Astrea é o sistema do FÓRUM, e o prédio do tribunal é o que o distingue de
     tudo que é nosso no trilho: nenhum outro ícone da mesa fala de fora da casa. */
  astrea: CourtHouseIcon,
  /* O robô é o rosto da IA quando ela RESPONDE PELO CASO — na fila da mesa, no
     cabeçalho do atendimento e no trilho. `AiUserIcon` (uma pessoa com faísca)
     dizia o contrário do que precisa dizer ali: que quem está no comando não é
     gente. */
  agenteIa: RoboticIcon,
  /* O assistente com quem se CONVERSA (a tela de Assistentes, o pedido feito a
     partir de uma mensagem): um balão, porque o que ele faz é conversar. */
  assistente: ChatBotIcon,
  responder: ReplyIcon,
  reagir: SmileIcon,
  editar: Edit02Icon,
  apagar: Delete02Icon,
  encaminhar: Share01Icon,
  fixar: PinIcon,
  silenciar: VolumeOffIcon,
  arquivar: Archive02Icon,
  /* Enquete: barras, que é como o WhatsApp desenha o resultado dela. */
  enquete: ChartBarLineIcon,
  /* O ponto no mapa que o cliente mandou. */
  local: Location01Icon,
  /* "Esta mensagem foi apagada" — a bolha fica, o conteúdo não. */
  proibido: MessageCancel01Icon,
  /* O "+" da barra de escrever: o que existe além de digitar. */
  somar: Add01Icon,
  /* O manual da mesa. */
  ajuda: HelpCircleIcon,
  /* Publicações é o JORNAL: o que o diário oficial publicou. Não a balança nem o
     martelo — os dois dizem "justiça" em geral, e o que esta tela mostra é a
     página do diário do dia. */
  publicacoes: NewspaperIcon,
  /* Criativos é o ANÚNCIO: de onde o lead veio. O megafone e não o gráfico —
     gráfico diria "números" em geral, e a tela é sobre cada anúncio. */
  criativos: Megaphone01Icon,
};

/**
 * As entradas do trilho lateral (e da barra do celular, que é o mesmo mapa).
 *
 * `curto` é o texto de 9px sob o ícone na barra do celular, a única navegação
 * que existe ali. É o MESMO nome da vista, encurtado — como "Docs" e "Reunião"
 * já eram: "Chat" dava dois nomes à mesma vista, um no computador e outro no
 * telefone, e em inglês. Abreviar "Atendimentos" é o que cabe em 45px; trocar
 * por "Conversas" seria outro conceito da casa (a conversa é a linha do WhatsApp,
 * o atendimento é o caso) no rótulo de `/atendimentos`.
 *
 * Mexer em `rotulo` mexe na demonstração guiada: ela aponta a seta por
 * `[aria-label="…"]` (`demonstracao/roteiro.ts`), e o alvo que não casa deixa o
 * primeiro passo apontando para o nada, em silêncio. `roteiro.test.jsx` prende
 * esse par.
 */
/*
 * As vistas da mesa, na ordem, em três grupos.
 *
 * `grupo` não é enfeite: onze ícones seguidos são uma lista sem forma, e a
 * pessoa varre os onze todas as vezes porque nada diz onde acaba o trabalho do
 * dia e onde começa a configuração do escritório. O trilho separa por grupo, e
 * o celular mostra `trabalho` na barra e o resto atrás de "Mais" — onze abas de
 * 35px numa tela de 412 punham rótulo em cima de rótulo.
 */
/*
 * `pode` é a permissão que a vista EXIGE — a mesma chave que a rota agregada
 * dela declara em `src/rotas/`.
 *
 * Mora aqui porque o trilho e a paleta são as duas portas de cada lugar, e as
 * duas mostravam os onze ícones para todo mundo: quem só atende via "Fluxos",
 * clicava e recebia "Origem não autorizada" numa tela vazia. A permissão já
 * chegava na abertura da sessão (`usuario.permissoes`), e a tela guardava sem
 * usar. Porta que não abre não é porta — é erro com aparência de menu.
 */
export const RAIL = [
  { chave: "chats", rotulo: "Atendimentos", curto: "Atend.", icone: ICONES.chats, grupo: "trabalho", pode: "leads:ver" },
  /* UMA entrada para o dia do escritório, e não duas.
     "Tarefas e prazos" e "Agenda" eram o mesmo assunto visto de dois ângulos — o
     prazo é o mesmo `vence_em` nos dois —, e a pessoa tinha de escolher a tela
     antes de saber se a pergunta era "quem faz" ou "quando cai". Agora é um
     lugar com cinco visões (`?visao=quadro|lista|dia|semana|mes`), como o
     WhatsApp já fazia com a caixa e os contatos. */
  {
    chave: "tarefas",
    rotulo: "Tarefas e agenda",
    curto: "Tarefas",
    icone: ICONES.tarefas,
    grupo: "trabalho",
    pode: "registros:ver",
  },
  /* Publicações é do DIA, e é o item mais caro de perder: prazo do fórum não
     espera ninguém abrir a tela. Fica logo depois de Tarefas porque é de lá que
     o prazo nasce — a publicação é o gatilho, a tarefa é o trabalho. */
  {
    chave: "publicacoes",
    rotulo: "Publicações",
    curto: "Publ.",
    icone: ICONES.publicacoes,
    grupo: "trabalho",
    pode: "publicacoes:ver",
  },
  /* Contatos é do DIA e não do acervo: a carteira é onde se decide quem chamar
     hoje — quem está parado, quem ninguém pegou, quem tem documento faltando.
     A permissão é a da ficha (`registros:ver`), que é o que a rota exige. */
  {
    chave: "contatos",
    rotulo: "Contatos",
    curto: "Contatos",
    icone: ICONES.contatos,
    grupo: "trabalho",
    pode: "registros:ver",
  },
  /* Criativos é do DIA: o Felipe olha quase todo dia quantos leads cada
     anúncio trouxe e quantos fecharam (áudio de 24/09). A permissão é a dos
     números do escritório, que é o que a rota exige. */
  {
    chave: "criativos",
    rotulo: "Criativos",
    curto: "Criativos",
    icone: ICONES.criativos,
    grupo: "trabalho",
    pode: "painel:ver",
  },
  { chave: "calls", rotulo: "Reuniões", curto: "Reunião", icone: ICONES.calls, grupo: "acervo", pode: "alinhamento:ver" },
  { chave: "files", rotulo: "Documentos", curto: "Docs", icone: ICONES.files, grupo: "acervo", pode: "documentos:ver" },
  {
    chave: "rapidas",
    rotulo: "Respostas rápidas",
    curto: "Rápidas",
    icone: ICONES.rapidas,
    grupo: "acervo",
    pode: "leads:ver",
  },
  /* Os modelos do pacote de assinatura. Moravam só atrás de uma porta em IA do
     atendimento e ninguém achava (23/09): é texto da casa, como as respostas
     prontas — só que o cliente ASSINA. Custou o piso do trilho sem rolagem
     (ver `guardas.test.jsx`). A permissão de ver é a que a rota exige. */
  {
    chave: "contratos",
    rotulo: "Modelos de contrato",
    curto: "Contratos",
    icone: ICONES.contrato,
    grupo: "acervo",
    pode: "assinaturas:ver",
  },
  /* Assistentes é do ESCRITÓRIO e não do dia: conversar com o agente é operar o
     sistema, ao lado de Fluxos — que é a mesma família (o que a IA faz aqui). A
     permissão é a que as rotas de `/api/agents` já exigem. */
  {
    chave: "agentes",
    rotulo: "Assistentes",
    curto: "Agentes",
    icone: ICONES.assistente,
    grupo: "escritorio",
    pode: "automacao:agentes",
  },
  /* UMA porta para a IA do atendimento, e não duas.
     "Fluxos de atendimento" e "Agentes de IA" eram as duas metades da MESMA
     decisão — o fluxo diz quando a IA age, o agente diz quem fala —, e o motor,
     os freios e o conteúdo que ela usa moravam ainda noutros dois lugares. Hoje
     é um lugar com cinco seções (`?secao=agentes|quando|conhecimento|motor`),
     como Tarefas fez com a agenda. `/fluxos` continua de pé, redirecionando. */
  {
    chave: "agentes_ia",
    rotulo: "IA do atendimento",
    curto: "IA",
    icone: ICONES.agenteIa,
    grupo: "escritorio",
    pode: "agentes:ver",
  },
  /* O Astrea é do ESCRITÓRIO e não do dia: ninguém abre esta tela para
     trabalhar — abre quando desconfia que a integração caiu, o que é operar o
     sistema, ao lado de Assistentes e da IA. A permissão é a que a rota exige
     (`automacao:astrea`), a mesma de quem opera o Astrea pela nossa origem. */
  {
    chave: "astrea",
    rotulo: "Astrea",
    curto: "Astrea",
    icone: ICONES.astrea,
    grupo: "escritorio",
    pode: "automacao:astrea",
  },
  {
    chave: "team",
    rotulo: "Equipe e acessos",
    curto: "Equipe",
    icone: ICONES.equipe,
    grupo: "escritorio",
    pode: "equipe:ver",
  },
  /* Ajustes é do SISTEMA, não do dia — como a Ajuda, ele desce para o pé da
     coluna. Um lugar da lista é onde se trabalha ("os atendimentos", "as
     tarefas"); estes dois são onde se mexe na ferramenta, e misturados eles
     pagavam a mesma linha de 38px que a mesa. Notificações já era assim: é o
     sino no alto, ao lado da paleta, e não uma linha entre os lugares. */
  {
    chave: "settings",
    rotulo: "Ajustes",
    curto: "Ajustes",
    icone: ICONES.settings,
    grupo: "escritorio",
    pode: "config:ver",
    rodape: true,
  },
  /* Setores e Sistema também descem para o pé, pelo mesmo motivo de Ajustes:
     são onde se mexe na FERRAMENTA, não onde se trabalha — e o trilho não tem
     linha sobrando (ver `guardas.test.jsx`). Setores é do master e do dev
     (`workspaces:numeros` é só deles); Sistema, só do dev (`sistema:ver`). */
  {
    chave: "setores",
    rotulo: "Setores",
    curto: "Setores",
    icone: ICONES.matriz,
    grupo: "escritorio",
    pode: "workspaces:numeros",
    rodape: true,
  },
  {
    chave: "sistema",
    rotulo: "Sistema",
    curto: "Sistema",
    icone: ICONES.painel,
    grupo: "escritorio",
    pode: "sistema:ver",
    rodape: true,
  },
  /* Ajuda não exige permissão nenhuma, e é a única entrada assim: manual é de
     quem usa a mesa. Esconder o manual de quem tem menos acesso seria esconder
     justamente de quem mais precisa dele.

     `rodape` a tira da LISTA de lugares e a põe junto da conta, no pé: ela é
     consulta rara (uma vez por dúvida, não por dia) e estava ocupando uma linha
     de 38px na coluna que precisa caber inteira em qualquer altura de tela.
     Continua no `RAIL` — e portanto na paleta (⌘K) e na aba "Mais" do celular —,
     porque tirar do catálogo seria tirar as duas portas. */
  { chave: "ajuda", rotulo: "Ajuda", curto: "Ajuda", icone: ICONES.ajuda, grupo: "escritorio", rodape: true },
];

/**
 * O mesmo RAIL fatiado pelos grupos, na ordem em que eles aparecem.
 *
 * O título só aparece com o trilho aberto — fechado, o grupo se diz pelo vão e
 * pelo fio, porque legenda de 9px ao lado de ícone de 20 não se lê, se adivinha.
 */
export const GRUPOS_DO_RAIL = [
  { grupo: "trabalho", titulo: "O dia" },
  { grupo: "acervo", titulo: "Acervo" },
  { grupo: "escritorio", titulo: "Escritório" },
].map(({ grupo, titulo }) => ({ grupo, titulo, itens: RAIL.filter((i) => i.grupo === grupo) }));
/**
 * O trilho que ESTA pessoa alcança — e os grupos já sem os que ficaram vazios.
 *
 * `pode` é o `usePode()` de `estado.jsx`. Grupo sem item some inteiro: título de
 * seção sozinho ("Escritório") sobre um vão é pior que a ausência, porque parece
 * tela quebrada em vez de acesso que a pessoa não tem.
 */
/* Item sem `pode` é de todo mundo — hoje só a Ajuda. O filtro precisa dizer
   isso explicitamente: `pode(undefined)` responderia "não" e o manual sumiria
   do trilho de quem não é administrador. */
const alcanca = (pode) => (i) => !i.pode || pode(i.pode);

export const railDe = (pode) => ({
  itens: RAIL.filter(alcanca(pode)),
  /* `rodape` sai dos grupos: ele é lugar do catálogo, mas não da lista. */
  grupos: GRUPOS_DO_RAIL.map((g) => ({ ...g, itens: g.itens.filter((i) => !i.rodape).filter(alcanca(pode)) })).filter(
    (g) => g.itens.length,
  ),
  rodape: RAIL.filter((i) => i.rodape).filter(alcanca(pode)),
});

/**
 * O nome de cada lugar — tirado do `RAIL`, que é a fonte única.
 *
 * Era um dicionário à parte, e envelheceu: o trilho prometia "Fluxos de IA" e
 * isto dizia "Fluxos". Um lugar tem UM nome; a porta e a tela não podem
 * discordar, senão a pessoa hesita se clicou certo.
 */
export const TITULOS = {
  ...Object.fromEntries(RAIL.map((i) => [i.chave, i.rotulo])),
  /* Notificações não está no trilho (a porta é o sino), mas é lugar com nome —
     e sem isto a aba do navegador dizia "Mesa de atendimento". */
  avisos: "Notificações",
  /* Fora do trilho pelo mesmo motivo: a porta é a boas-vindas, a paleta e a ajuda. */
  comecar: "Primeiros passos",
};

/** Respostas prontas do atendimento. Texto do escritório, não regra. */
export const PRONTAS = [
  ["/prim", "Olá! Aqui é da Galera Advocacia. Recebemos seu contato e um advogado assume seu caso em até 30 minutos."],
  ["/hon", "Nossos honorários seguem a tabela da OAB e são cobrados por êxito: você só paga se ganharmos."],
  [
    "/docs",
    "Para dar entrada, preciso de RG, CPF, comprovante de residência e os documentos do caso. Pode enviar fotos por aqui.",
  ],
  [
    "/lgpd",
    "Seus dados são usados apenas para o seu atendimento jurídico, conforme a LGPD, e protegidos por sigilo profissional.",
  ],
  [
    "/aud",
    "Sua audiência está marcada. Compareça 20 minutos antes com documento com foto. Faço uma call de preparação na véspera.",
  ],
];
